<div class="intelligence">
  @if (loading) {
    <div class="state" role="status"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i> Loading CI report...</div>
  } @else if (report) {
    <div class="highlight">
      <div><i class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
        @if (report.verdict) { <strong>{{ report.verdict }}</strong> } @else { @for (tag of tags; track tag) { <strong>{{ tag }}</strong> } }
        @if (!tags.length) { <strong>Circled Intelligence (CI)</strong> }
      </div>
      <span>{{ report.propertyType ? report.propertyType + ' intelligence at a glance' : 'Property intelligence at a glance' }}</span>
    </div>

    <section class="panel verdict">
      <h2><i class="fa-solid fa-gem" aria-hidden="true"></i> Verdict</h2>
      <div class="verdict-content">
        <p>{{ report.summary || 'A verdict is not available for this property yet.' }}</p>
        @if (analystNote) {
          <div class="analyst-note"><h3>Analyst Note</h3><p>{{ analystNote }}</p></div>
        }
        <div class="badges">
          @for (tag of tags; track tag; let i = $index) {
            <span class="badge" [class.mint]="i % 3 === 0" [class.blue]="i % 3 === 2">{{ tag }}</span>
          }
        </div>
      </div>
    </section>



    @if (report.assessmentMetrics?.length) {
      <section class="panel">
        <h2>{{ report.propertyType === 'Independent House' ? 'Land & Building Assessment' : 'Land Assessment' }}</h2>
        <div class="metrics">
          @for (metric of report.assessmentMetrics; track metric.label) {
            <div class="metric"><span>{{ metric.label }}</span><strong>{{ metric.value }}</strong></div>
          }
        </div>
        @if (report.propertyType === 'Independent House') {
          <p class="footnote">Assessed Land Value + Building Value = Total Assessed Property Value.</p>
        }
      </section>
    }
    <div class="paired">
      @if (pricing.length) {
      <section class="panel">
        <h2><i class="fa-solid fa-table-cells" aria-hidden="true"></i> Pricing Benchmark</h2>
        <div class="metrics">
          @for (metric of pricing; track metric.label) {
            <div class="metric" [class.amber]="metric.label === 'Ask vs Benchmark'">
              <span>{{ displayLabel(metric.label) }}</span><strong>{{ metric.value }}</strong>
              @if (metric.label.includes('PSF')) { <small>per sq.ft.</small> }
            </div>
          }
        </div>
        @if (value(report.pricing, 'Revised Benchmark PSF') !== 'Not available') {
          <p class="footnote">Final CI benchmark <b>{{ value(report.pricing, 'Revised Benchmark PSF') }}</b> / sq.ft.</p>
        }
      </section>
      }
      @if (udsMetrics.length) {
        <section class="panel">
          <h2><i class="fa-regular fa-building" aria-hidden="true"></i> Land Share (UDS)</h2>
          <div class="metrics">
            @for (metric of udsMetrics; track metric.label) {
              <div class="metric"><span>{{ displayLabel(metric.label) }}</span><strong>{{ metric.value }}</strong>
                @if (metric.label === 'UDS Rate') { <small>per sq.ft.</small> }
              </div>
            }
          </div>
        </section>
      }
      @if (rentalMetrics.length) {
        <section class="panel">
          <h2><i class="fa-solid fa-building-columns" aria-hidden="true"></i> Rental Information</h2>
          <div class="metrics">
            @for (metric of rentalMetrics; track metric.label) {
              <div class="metric"><span>{{ metric.label }}</span><strong>{{ metric.value }}</strong></div>
            }
          </div>
        </section>
      }
      @if (valuation.length) {
      <section class="panel">
        <h2><i class="fa-solid fa-building-columns" aria-hidden="true"></i> {{ valuationTitle }}</h2>
        <div class="metrics">
          @for (metric of valuation; track metric.label) {
            <div class="metric" [class.amber]="metric.label === 'Rental Yield'">
              <span>{{ displayLabel(metric.label) }}</span><strong>{{ metric.value }}</strong>
              @if (metric.label === 'Monthly Rent' && value(report.valuation, 'Annual Rent') !== 'Not available') { <small>{{ value(report.valuation, 'Annual Rent') }} PA</small> }
              @if (metric.label === 'Adjusted Current Value (UDS)') { <small>Based on UDS area</small> }
              @if (metric.label === 'UDS Support vs Close High') { <small>Land-value support</small> }
            </div>
          }
        </div>
      </section>
      }
    </div>

    @if (report.adjustmentBreakdown; as breakdown) {
      <section class="panel adjustments">
        <span class="eyebrow">{{ isLandProperty ? 'Land Evidence' : 'Benchmark Evidence' }}</span>
        <h2>{{ isLandProperty ? 'Land Adjustments' : 'Property Adjustments' }}</h2>
        <div class="metrics">
          @if (breakdown.base.value !== 'Not available') { <div class="metric"><span>{{ breakdown.base.label }}</span><strong>{{ breakdown.base.value }}</strong></div> }
          @if (breakdown.total) { <div class="metric"><span>Total Adjustment</span><strong>{{ breakdown.total }}</strong></div> }
          @if (breakdown.final.value !== 'Not available') { <div class="metric"><span>{{ breakdown.final.label }}</span><strong>{{ breakdown.final.value }}</strong></div> }
        </div>
        <div class="benchmark-inputs">
          @for (adjustment of breakdown.rows; track adjustment.label) {
            <div><strong>{{ adjustment.label }}: {{ adjustment.percentage }}</strong><p>{{ adjustment.reason || 'Reason not supplied in this report.' }}</p></div>
          }
        </div>
        @if (!breakdown.rows.length && breakdown.total && breakdown.total !== '0.0%') { <p class="transaction-note">The report supplies the total adjustment but does not include the individual adjustment details.</p> }
      </section>
    }

    <div class="paired">
      @if (report.offers.length) {
      <section class="panel">
        <h2><i class="fa-regular fa-gem" aria-hidden="true"></i> Offer Guidance</h2>
        <div class="offer-group">
          @if (psfOffers.length) {
          <h3>PSF Range</h3>
          <div class="metrics">
            @for (metric of psfOffers; track metric.label; let first = $first) {
              <div class="metric" [class.amber]="first"><span>{{ displayLabel(metric.label) }}</span><strong>{{ metric.value }}</strong></div>
            }
          </div>
          }
          @if (valueOffers.length) {
          <h3>Total Value</h3>
          <div class="metrics">
            @for (metric of valueOffers; track metric.label; let first = $first) {
              <div class="metric" [class.amber]="first"><span>{{ displayLabel(metric.label) }}</span><strong>{{ metric.value }}</strong></div>
            }
          </div>
          }
        </div>
      </section>
      }
    </div>

    @if (usesPrimaryComparables) {
      <section class="panel analysis transactions">
        <span class="eyebrow">Pricing Evidence</span>
        <h2>Comparable Project Evidence</h2>
        <p class="latest-transaction">Sufficient registered transaction history is not available for this project. To establish a market benchmark, Circled has referenced similar projects within the same micro-market with comparable positioning and property characteristics.</p>
        @if (report.comparableProjects?.length) {
          <div class="comparable-scroll" tabindex="0" role="region" aria-label="Comparable project evidence">
            <table>
              <thead><tr><th scope="col">Comparable Project</th><th scope="col">Benchmark / Sq.Ft.</th><th scope="col">Reference Date</th></tr></thead>
              <tbody>
                @for (project of report.comparableProjects; track $index) {
                  <tr><td data-label="Comparable project">{{ project.name }}</td><td data-label="Benchmark / sq.ft.">{{ project.psf }}</td><td data-label="Reference date">{{ project.reference }}</td></tr>
                }
              </tbody>
            </table>
          </div>
          @for (project of report.comparableProjects; track $index) {
            @if (project.transactions?.length) {
              <details class="comparable-support">
                <summary>{{ project.name }} — supporting transaction evidence</summary>
                <div class="comparable-scroll">
                  <table><thead><tr><th scope="col">Date</th><th scope="col">Source</th><th scope="col">Rate / sq.ft.</th></tr></thead><tbody>
                    @for (transaction of project.transactions; track $index) { <tr><td data-label="Date">{{ transaction.date }}</td><td data-label="Source">{{ transaction.source }}</td><td data-label="Rate / sq.ft.">{{ transaction.psf }}</td></tr> }
                  </tbody></table>
                </div>
              </details>
            }
          }
          <p class="transaction-note">These comparable projects are used as market references where direct project-level transaction history is limited.</p>
        } @else {
          <p class="transaction-note">Comparable project details have not been supplied in this report.</p>
        }
        @if (primaryAnalysisNote) { <p class="insight">{{ primaryAnalysisNote }}</p> }
      </section>
    }

    @if (!usesPrimaryComparables && report.transactions?.length) {
    <section class="panel analysis transactions">
      <div class="transactions-heading">
        <div>
          <span class="eyebrow">Pricing Evidence</span>
          <h2>{{ usesResaleTransactions ? 'Registered Transaction History' : 'Transaction History (' + (report.transactions?.length ?? 0) + ' records)' }}</h2>
        </div>
        <span class="ci-data-badge">CI Data</span>
      </div>
      @if (usesResaleTransactions) {
        <p class="transaction-helper">Recent registered transactions within this property are used to understand the prevailing market rate and establish a reliable pricing benchmark.</p>
      }
      @if (report.transactions?.length) {
        <div class="transactions-scroll" tabindex="0" role="region" aria-label="Transaction history">
          <table>
            <thead><tr><th scope="col">Date</th><th scope="col">Source</th>@if (!usesResaleTransactions) { <th scope="col">Transaction Value</th> }<th scope="col">Rate / sq.ft.</th></tr></thead>
            <tbody>
              @for (transaction of report.transactions; track $index) {
                <tr><td>{{ transaction.date }}</td><td>{{ transaction.source || 'Not available' }}</td>@if (!usesResaleTransactions) { <td>{{ transaction.price }}</td> }<td>{{ transaction.psf }}</td></tr>
              }
            </tbody>
          </table>
        </div>
        <div class="transaction-cards" aria-label="Transaction history">
          @for (transaction of report.transactions; track $index) {
            <dl class="transaction-card" [class.is-resale]="usesResaleTransactions">
              <div class="transaction-date"><dt><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><rect x="4" y="5" width="16" height="16" rx="3"/><path d="M8 3v4m8-4v4M4 11h16"/></svg>Date</dt><dd>{{ transaction.date }}</dd></div>
              <div class="transaction-source"><dt><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H5v18h14V8Zm0 0v5h5M8 12h8m-8 4h6"/></svg>Source</dt><dd>{{ transaction.source || 'Not available' }}</dd></div>
              @if (!usesResaleTransactions) { <div><dt>Transaction Value</dt><dd>{{ transaction.price }}</dd></div> }
              <div class="transaction-psf"><dt>Rate / sq.ft.</dt><dd>{{ transaction.psf }}</dd></div>
            </dl>
          }
        </div>
        @if (report.transactions?.[0]?.date && report.transactions?.[0]?.date !== 'Not available') {
          <p class="latest-transaction">{{ usesResaleTransactions ? 'Latest registered transaction:' : 'Latest transaction:' }} <strong>{{ report.transactions?.[0]?.date }}</strong></p>
        }
        @if (resaleAnalysisNote) { <p class="insight">{{ resaleAnalysisNote }}</p> }
        @if (report.transactionAreaEstimated) {
          <p class="transaction-note">Area uses the subject property's size. Total prices are estimated from the recorded price per sq ft.</p>
        }
      } @else {
        <p class="latest-transaction">No registered transactions available for this property yet.</p>
      }
    </section>

    }
    <section class="panel analysis">
      <span class="eyebrow">Valuation</span>
      <h2><i class="fa-solid fa-indian-rupee-sign" aria-hidden="true"></i> {{ isLandProperty ? 'Fair Value' : 'Fair Value & Benchmark' }}</h2>
      @if (!isLandProperty) {
      <div class="benchmark-explanation">
        <h3>How Circled arrives at the benchmark</h3>
        @if (!isLandProperty) {
        <div class="benchmark-reasoning">
          <p><strong>Transaction evidence:</strong> We first look at the most relevant recent transaction data available for the property. Where direct history is limited, comparable projects in the same micro-market are used.</p>
          <p><strong>Market benchmark:</strong> This transaction evidence helps establish the prevailing market rate for a similar property in the location.</p>
          <p><strong>Property-specific adjustment:</strong> We then consider characteristics specific to this property to arrive at a more relevant benchmark.</p>
          <p><strong>Estimated benchmark value:</strong> Based on the Circled Benchmark and the property area, we show the estimated benchmark value for the subject property.</p>
        </div>
        }
        <p class="benchmark-note">{{ benchmarkContext }}</p>
        @if (evidence.length) {
          <dl class="benchmark-inputs">
            @for (metric of evidence; track metric.label) {
              <div><dt>{{ displayLabel(metric.label) }}</dt><dd>{{ metric.value }}</dd></div>
            }
          </dl>
        }
        @if (finalBenchmark !== 'Not available') {
          <p class="benchmark-note"><strong>Final CI benchmark: {{ finalBenchmark }} per sq.ft.</strong> Use this as a price reference alongside the property's condition and your offer guidance. It is an estimate, not a guaranteed selling price.</p>
        }
      </div>
      } @else {
        <p class="transaction-helper">{{ benchmarkContext }}</p>
      }
      @if (report.analysis) {
      <div class="analysis-metrics">
        @if (report.analysis.askingPrice != null) { <div><span>Asking Price</span><strong>{{ compact(report.analysis.askingPrice) }}</strong></div> }
        @if (report.analysis.fairValue != null) { <div><span>{{ isLandProperty ? (report.propertyType === 'Independent House' ? 'Total Assessed Property Value' : 'Assessed Land Value') : 'Base Benchmark Value' }}</span><strong class="purple">{{ compact(report.analysis.fairValue) }}</strong></div> }
        @if (report.analysis.difference != null) { <div><span>Difference</span><strong class="orange">{{ difference }}</strong></div> }
      </div>
      @if (report.analysis.difference != null) {
        <p class="insight"><i class="fa-regular fa-lightbulb" aria-hidden="true"></i> Asking price compared with {{ isLandProperty ? 'the assessed property value' : 'the Circled benchmark' }}: <b>{{ difference }}</b>.</p>
      }
      }
    </section>

    @if (evidence.length) {
    <section class="panel analysis">
      <span class="eyebrow">Evidence</span>
      <h2><i class="fa-solid fa-chart-line" aria-hidden="true"></i> {{ hasTransactionHistory ? 'Transaction Evidence' : 'Available Price References' }}</h2>
      <div class="evidence">
        @for (metric of evidence; track metric.label) {
          <div class="metric"><span>{{ displayLabel(metric.label) }}</span><strong>{{ metric.value }}</strong></div>
        }
      </div>
    </section>

    }
  } @else {
    <div class="state" role="status"><i class="fa-regular fa-file-lines" aria-hidden="true"></i>{{ error || 'CI data is not available for this property yet.' }}</div>
  }
</div>
